import React, { useState, useContext } from "react";
import { Download, Info, AlertCircle, Activity, CheckCircle, Eye } from "lucide-react";
import * as XLSX from "xlsx";
import { mo3tiThemes } from "../../../../../constant/theme";
import Mo3tiButton from "../../layout/Mo3tiButton";
import { apiFetch } from "../../../../../context/AuthContext";

const ExcelImportModal = ({
  onClose,
  onImportComplete,
  API_BASE_URL,
  t,
  language,
  theme,
  isRTL,
  importStep,
  setImportStep,
  importFile,
  setImportFile,
  importData,
  setImportData,
  importLoading,
  setImportLoading,
  importError,
  setImportError,
  importProgress,
  setImportProgress,
  importResults,
  setImportResults,
  importSettings,
  setImportSettings,
}) => {
    const currentTheme = mo3tiThemes[theme] || mo3tiThemes.dark;

    const columnMapping = {
      "Client Code": "clientCode",
      "Infinity Code": "infinityCode",
      "Employee Name": "employeeName",
      "Payroll Start date": "payrollStartDate",
      "Payroll End date": "payrollEndDate",
      "Working days": "workingDays",
      "Absence Days": "absenceDays",
      "Absence Days Value": "absenceDaysValue",
      "unpaid & deduction days": "unpaidDeductionDays",
      "unpaid & deduction value": "unpaidDeductionValue",
      "OverTime Days": "overtimeDays",
      "OverTime value": "overtimeValue",
      "Bonus Days": "bonusDays",
      "Bonus Value": "bonusValue",
      expenses: "expenses",
      "Penality Days": "penalityDays",
      "Penality Days Value": "penalityDaysValue",
      "25% Deduction (Sick Leave) Days": "sickLeave25DeductionDays",
      "25% Deduction (Sick Leave) Value": "sickLeave25DeductionValue",
      Achievement: "achievement",
      "Actual Net Salary": "netSalary",
      Basic: "basicSalary",
    };

    const handleFileSelect = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];

      if (
        !validTypes.includes(file.type) &&
        !file.name.match(/\.(xlsx|xls|csv)$/i)
      ) {
        setImportError(
          "Please upload a valid Excel file (.xlsx, .xls, or .csv)",
        );
        return;
      }

      setImportFile(file);
      setImportError(null);
      setImportLoading(true);

      try {
        const reader = new FileReader();

        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
              raw: false,
              defval: null,
            });

            console.log("📊 Parsed Excel data:", jsonData);

            if (jsonData.length === 0) {
              throw new Error("Excel file is empty or has no valid data");
            }

            const transformedData = jsonData.map((row, index) => {
              const mapped = {};

              Object.keys(columnMapping).forEach((excelCol) => {
                const dbField = columnMapping[excelCol];
                if (
                  row[excelCol] !== undefined &&
                  row[excelCol] !== null &&
                  row[excelCol] !== ""
                ) {
                  if (
                    [
                      "workingDays",
                      "absenceDays",
                      "unpaidDeductionDays",
                      "overtimeDays",
                      "bonusDays",
                      "penalityDays",
                      "sickLeave25DeductionDays",
                    ].includes(dbField)
                  ) {
                    mapped[dbField] = parseInt(row[excelCol]) || 0;
                  } else if (
                    [
                      "absenceDaysValue",
                      "unpaidDeductionValue",
                      "overtimeValue",
                      "bonusValue",
                      "expenses",
                      "penalityDaysValue",
                      "sickLeave25DeductionValue",
                      "achievement",
                      "netSalary",
                    ].includes(dbField)
                  ) {
                    mapped[dbField] = parseFloat(row[excelCol]) || 0;
                  } else if (
                    ["payrollStartDate", "payrollEndDate"].includes(dbField)
                  ) {
                    const dateValue = row[excelCol];
                    if (dateValue) {
                      if (typeof dateValue === "number") {
                        const excelEpoch = new Date(1899, 11, 30);
                        const date = new Date(
                          excelEpoch.getTime() + dateValue * 86400000,
                        );
                        mapped[dbField] = date.toISOString().split("T")[0];
                      } else {
                        const date = new Date(dateValue);
                        if (!isNaN(date)) {
                          mapped[dbField] = date.toISOString().split("T")[0];
                        }
                      }
                    }
                  } else {
                    mapped[dbField] = String(row[excelCol]);
                  }
                }
              });

              if (mapped.payrollStartDate) {
                const date = new Date(mapped.payrollStartDate);
                mapped.year = date.getFullYear();
                mapped.month = date.getMonth() + 1;
              } else {
                mapped.year = importSettings.defaultYear;
                mapped.month = importSettings.defaultMonth;
              }

              const errors = [];
              if (!mapped.clientCode && !mapped.infinityCode) {
                errors.push("Missing Client Code (or Infinity Code)");
              }
              if (!mapped.year || !mapped.month) {
                errors.push("Missing or invalid payroll period");
              }

              return {
                rowNumber: index + 2,
                data: mapped,
                errors: errors,
                isValid: errors.length === 0,
              };
            });

            console.log("✅ Transformed data:", transformedData);

            setImportData(transformedData);
            setImportStep(2);
          } catch (parseError) {
            console.error("Parse error:", parseError);
            setImportError(`Failed to parse Excel file: ${parseError.message}`);
          } finally {
            setImportLoading(false);
          }
        };

        reader.onerror = () => {
          setImportError("Failed to read file");
          setImportLoading(false);
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error("File processing error:", error);
        setImportError(`Error processing file: ${error.message}`);
        setImportLoading(false);
      }
    };

    const handleImport = async () => {
      if (!importData || importData.length === 0) {
        setImportError("No data to import");
        return;
      }

      setImportLoading(true);
      setImportProgress(0);
      setImportError(null);
      setImportResults(null);

      const results = {
        total: importData.length,
        successful: 0,
        failed: 0,
        skipped: 0,
        errors: [],
      };

      try {
        const validRecords = importData
          .filter((row) => row.isValid)
          .map((row) => row.data);

        const invalidCount = importData.length - validRecords.length;
        results.skipped = invalidCount;

        if (validRecords.length === 0) {
          throw new Error(
            `No valid records to import (${invalidCount} records failed validation)`,
          );
        }

        console.log(`Importing ${validRecords.length} valid records...`);

        setImportProgress(20);

        const response = await apiFetch(
          `${API_BASE_URL}/payrollX/import/excel`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              payrollRecords: validRecords,
            }),
            timeout: 300000,
          },
        );

        setImportProgress(80);

        if (!response.ok) {
          let errorData;
          try {
            const contentType = response.headers.get("content-type");
            errorData =
              contentType && contentType.includes("application/json")
                ? await response.json()
                : await response.text();
          } catch {
            errorData = `HTTP ${response.status}: ${response.statusText}`;
          }

          const errorMessage =
            errorData?.error || errorData?.message || errorData;
          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (!result.data) {
          throw new Error("Invalid response format from server");
        }

        results.successful = result.data.successful || 0;
        results.failed = result.data.failed || 0;
        results.total = result.data.total || validRecords.length;
        results.errors = result.data.errors || [];

        console.log("Import completed:", results);

        setImportResults(results);
        setImportStep(3);
        setImportProgress(100);

        if (results.failed > 0) {
          console.warn(
            `Import completed with ${results.failed} failures. Check errors for details.`,
          );
        }
      } catch (error) {
        console.error("Import error:", error);

        const errorMessage =
          error.message || "An unexpected error occurred during import";

        setImportError(errorMessage);
        setImportResults({
          ...results,
          successful: 0,
          failed: results.total,
        });

        setImportStep(2);
      } finally {
        setImportLoading(false);
        setImportProgress(100);
      }
    };

    const modalStyles = {
      overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: "20px",
        backdropFilter: "blur(4px)",
      },
      modal: {
        background:
          theme === "dark"
            ? "rgba(30, 41, 59, 1.6)"
            : "rgba(255, 255, 255, 0.9)",
        border: `1px solid ${currentTheme.colors.border}`,
        borderRadius: "16px",
        width: "100%",
        maxWidth: "1100px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        //boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
      },
      header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "24px 32px",
        borderBottom: `1px solid ${currentTheme.colors.border}`,
        flexShrink: 0,
      },
      title: {
        color: currentTheme.colors.textPrimary,
        fontSize: "24px",
        fontWeight: "700",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: "12px",
      },
      closeButton: {
        background: "transparent",
        border: `1px solid ${currentTheme.colors.border}`,
        borderRadius: "8px",
        padding: "8px",
        cursor: "pointer",
        color: currentTheme.colors.textSecondary,
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      content: {
        padding: "32px",
        overflowY: "auto",
        flex: 1,
      },
      footer: {
        display: "flex",
        gap: "12px",
        justifyContent: "flex-end",
        padding: "20px 32px",
        borderBottom: `1px solid ${currentTheme.colors.border}`,
        flexShrink: 0,
      },
      uploadArea: {
        border: `2px dashed ${currentTheme.colors.border}`,
        borderRadius: "12px",
        padding: "48px 32px",
        textAlign: "center",
        background: `${currentTheme.colors.primary}05`,
        cursor: "pointer",
        transition: "all 0.3s ease",
        outline: "none",
        display: "block",
      },
      statsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 4fr))",
        gap: "16px",
        marginBottom: "24px",
      },
      statCard: {
        padding: "20px",
        background: currentTheme.colors.inputBackground,
        borderRadius: "12px",
        border: `1px solid ${currentTheme.colors.border}`,
        textAlign: "center",
      },
      statValue: {
        fontSize: "32px",
        fontWeight: "700",
        marginBottom: "8px",
      },
      statLabel: {
        color: currentTheme.colors.textSecondary,
        fontSize: "14px",
        fontWeight: "500",
      },
      infoBox: {
        padding: "16px",
        background: `${currentTheme.colors.info}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.info}30`,
        marginTop: "24px",
      },
      warningBox: {
        padding: "16px",
        background: `${currentTheme.colors.warning}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.warning}30`,
        marginTop: "16px",
      },
      errorBox: {
        padding: "16px",
        background: `${currentTheme.colors.danger}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.danger}30`,
        marginTop: "16px",
      },
      successBox: {
        padding: "16px",
        background: `${currentTheme.colors.success}10`,
        borderRadius: "10px",
        border: `1px solid ${currentTheme.colors.success}30`,
        marginBottom: "16px",
      },
      table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "14px",
        background: currentTheme.colors.cardBackground,
      },
      tableContainer: {
        maxHeight: "400px",
        overflowY: "auto",
        border: `1px solid ${currentTheme.colors.border}`,
        borderRadius: "10px",
        marginTop: "16px",
      },
    };
    const enhancedTableStyles = (currentTheme, isRTL) => ({
      tableContainer: {
        overflowX: "auto",
        borderRadius: "20px",
        border: `2px solid ${currentTheme.colors.borderLight}`,
        background: currentTheme.colors.cardBackground,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      },
      table: {
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: "0",
        fontSize: "14px",
        direction: isRTL ? "rtl" : "ltr",
      },
      thead: {
        background: ` (135deg, ${currentTheme.colors.primary}0, ${currentTheme.colors.primary}05)`,
        position: "sticky",
        top: 0,
        zIndex: 10,
      },
      th: {
        padding: "20px 16px",
        textAlign: isRTL ? "right" : "left",
        color: currentTheme.colors.textPrimary,
        fontWeight: "800",
        fontSize: "13px",
        textTransform: "uppercase",
        letterSpacing: "1.2px",
        borderBottom: `3px solid ${currentTheme.colors.primary}40`,
        whiteSpace: "nowrap",
        background: currentTheme.colors.inputBackground,
      },
      thCenter: {
        textAlign: "center",
      },
      tr: {
        background: currentTheme.colors.cardBackground,
        borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
      },
      td: {
        padding: "20px 16px",
        borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
        verticalAlign: "middle",
      },
      employeeCell: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
      },
      employeeName: {
        color: currentTheme.colors.textPrimary,
        fontSize: "16px",
        fontWeight: "700",
        marginBottom: "4px",
      },
      employeeDepartment: {
        color: currentTheme.colors.textSecondary,
        fontSize: "13px",
      },
      employeePosition: {
        color: currentTheme.colors.textMuted,
        fontSize: "12px",
      },
      salaryValue: {
        color: currentTheme.colors.textPrimary,
        fontSize: "15px",
        fontWeight: "700",
        fontFamily: "monospace",
      },
      allowanceValue: {
        color: currentTheme.colors.success,
        fontSize: "15px",
        fontWeight: "700",
        fontFamily: "monospace",
      },
      deductionValue: {
        color: currentTheme.colors.danger,
        fontSize: "15px",
        fontWeight: "700",
        fontFamily: "monospace",
      },
      netSalaryValue: {
        color: currentTheme.colors.success,
        fontSize: "18px",
        fontWeight: "900",
        fontFamily: "monospace",
        textShadow: `0 2px 4px ${currentTheme.colors.success}30`,
      },
      subText: {
        fontSize: "11px",
        color: currentTheme.colors.textMuted,
        marginTop: "4px",
      },
      actionButtons: {
        display: "flex",
        gap: "8px",
        justifyContent: "center",
      },
    });

    const renderStepContent = () => {
      switch (importStep) {
        case 1:
          return (
            <div>
              <input
                type="file"
                id="excel-file-input"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />

              <label
                htmlFor="excel-file-input"
                style={modalStyles.uploadArea}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor =
                    currentTheme.colors.primary;
                  e.currentTarget.style.background = `${currentTheme.colors.primary}15`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor =
                    currentTheme.colors.border;
                  e.currentTarget.style.background = `${currentTheme.colors.primary}05`;
                }}
              >
                <Download
                  size={48}
                  color={currentTheme.colors.primary}
                  style={{ marginBottom: "16px" }}
                />
                <h3
                  style={{
                    color: currentTheme.colors.textPrimary,
                    marginBottom: "8px",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  {importFile ? importFile.name : t("uploadFile")}
                </h3>
                <p
                  style={{
                    color: currentTheme.colors.textSecondary,
                    marginBottom: "16px",
                    fontSize: "14px",
                  }}
                >
                  {t("supportedFormats")}
                </p>
                {importFile && (
                  <div
                    style={{
                      padding: "8px 16px",
                      background: currentTheme.colors.success,
                      color: "#fff",
                      borderRadius: "6px",
                      display: "inline-block",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    ✓ {(importFile.size / 1024).toFixed(2)} KB
                  </div>
                )}
              </label>

              {importError && (
                <div style={modalStyles.errorBox}>
                  <AlertCircle
                    size={18}
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    {importError}
                  </span>
                </div>
              )}

              {importLoading && (
                <div style={{ marginTop: "20px", textAlign: "center" }}>
                  <Activity
                    size={32}
                    color={currentTheme.colors.primary}
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                  <p
                    style={{
                      color: currentTheme.colors.textSecondary,
                      marginTop: "12px",
                      fontSize: "14px",
                    }}
                  >
                    {t("processingFile")}
                  </p>
                </div>
              )}

              <div style={modalStyles.infoBox}>
                <h4
                  style={{
                    color: currentTheme.colors.textPrimary,
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                  }}
                >
                  <Info size={18} />
                  {t("expectedFormat")}
                </h4>
                <p
                  style={{
                    color: currentTheme.colors.textSecondary,
                    fontSize: "13px",
                    lineHeight: "1.6",
                    margin: 0,
                  }}
                >
                  {t("formatDescription")}
                </p>
              </div>

              <div
                style={{
                  marginTop: "20px",
                  padding: "16px",
                  background: currentTheme.colors.inputBackground,
                  borderRadius: "10px",
                  border: `1px solid ${currentTheme.colors.border}`,
                }}
              >
                <h4
                  style={{
                    color: currentTheme.colors.textPrimary,
                    marginBottom: "12px",
                    fontSize: "15px",
                    fontWeight: "600",
                  }}
                >
                  {t("importSettings")}
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: currentTheme.colors.textSecondary,
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={importSettings.skipErrors}
                      onChange={(e) =>
                        setImportSettings({
                          ...importSettings,
                          skipErrors: e.target.checked,
                        })
                      }
                    />
                    {t("skipErrors")}
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: currentTheme.colors.textSecondary,
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={importSettings.calculateMissing}
                      onChange={(e) =>
                        setImportSettings({
                          ...importSettings,
                          calculateMissing: e.target.checked,
                        })
                      }
                    />
                    {t("autoCalculate")}
                  </label>
                </div>
              </div>
            </div>
          );

        case 2:
          const validRows = importData?.filter((row) => row.isValid) || [];
          const invalidRows = importData?.filter((row) => !row.isValid) || [];

          return (
            <div>
              <div style={modalStyles.statsGrid}>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.primary,
                    }}
                  >
                    {importData?.length || 0}
                  </div>
                  <div style={modalStyles.statLabel}>{t("totalRows")}</div>
                </div>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.success,
                    }}
                  >
                    {validRows.length}
                  </div>
                  <div style={modalStyles.statLabel}>{t("validRows")}</div>
                </div>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.danger,
                    }}
                  >
                    {invalidRows.length}
                  </div>
                  <div style={modalStyles.statLabel}>{t("errorRows")}</div>
                </div>
              </div>

              <div style={modalStyles.tableContainer}>
                <table style={modalStyles.table}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: currentTheme.colors.textPrimary,
                          background: currentTheme.colors.inputBackground,
                          borderBottom: `1px solid ${currentTheme.colors.border}`,
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {t("row")}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: currentTheme.colors.textPrimary,
                          background: currentTheme.colors.inputBackground,
                          borderBottom: `1px solid ${currentTheme.colors.border}`,
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {t("code")}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: currentTheme.colors.textPrimary,
                          background: currentTheme.colors.inputBackground,
                          borderBottom: `1px solid ${currentTheme.colors.border}`,
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {t("name")}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: currentTheme.colors.textPrimary,
                          background: currentTheme.colors.inputBackground,
                          borderBottom: `1px solid ${currentTheme.colors.border}`,
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {t("period")}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: currentTheme.colors.textPrimary,
                          background: currentTheme.colors.inputBackground,
                          borderBottom: `1px solid ${currentTheme.colors.border}`,
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {t("status")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {importData?.map((row, index) => (
                      <tr
                        key={index}
                        style={{
                          background: row.isValid
                            ? "transparent"
                            : `${currentTheme.colors.danger}08`,
                        }}
                      >
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: `1px solid ${currentTheme.colors.border}`,
                            color: currentTheme.colors.textSecondary,
                            fontSize: "13px",
                          }}
                        >
                          {row.rowNumber}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: `1px solid ${currentTheme.colors.border}`,
                            color: currentTheme.colors.textPrimary,
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          {row.data.clientCode || row.data.infinityCode || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: `1px solid ${currentTheme.colors.border}`,
                            color: currentTheme.colors.textSecondary,
                            fontSize: "13px",
                          }}
                        >
                          {row.data.employeeName || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: `1px solid ${currentTheme.colors.border}`,
                            color: currentTheme.colors.textSecondary,
                            fontSize: "13px",
                          }}
                        >
                          {row.data.year}/{row.data.month}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: `1px solid ${currentTheme.colors.border}`,
                            fontSize: "13px",
                          }}
                        >
                          {row.isValid ? (
                            <span
                              style={{
                                color: currentTheme.colors.success,
                                fontWeight: "600",
                              }}
                            >
                              ✓ {t("valid")}
                            </span>
                          ) : (
                            <span
                              style={{
                                color: currentTheme.colors.danger,
                                fontWeight: "500",
                                fontSize: "12px",
                              }}
                            >
                              {row.errors.join(", ")}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {invalidRows.length > 0 && (
                <div style={modalStyles.warningBox}>
                  <AlertCircle
                    size={18}
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    {t("errorsWillBeSkipped").replace(
                      "{count}",
                      invalidRows.length,
                    )}
                  </span>
                </div>
              )}
            </div>
          );

        case 3:
          return (
            <div>
              <div style={modalStyles.statsGrid}>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.primary,
                    }}
                  >
                    {importResults?.total || 0}
                  </div>
                  <div style={modalStyles.statLabel}>{t("total")}</div>
                </div>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.success,
                    }}
                  >
                    {importResults?.successful || 0}
                  </div>
                  <div style={modalStyles.statLabel}>{t("successful")}</div>
                </div>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.danger,
                    }}
                  >
                    {importResults?.failed || 0}
                  </div>
                  <div style={modalStyles.statLabel}>{t("failed")}</div>
                </div>
                <div style={modalStyles.statCard}>
                  <div
                    style={{
                      ...modalStyles.statValue,
                      color: currentTheme.colors.warning,
                    }}
                  >
                    {importResults?.skipped || 0}
                  </div>
                  <div style={modalStyles.statLabel}>{t("skipped")}</div>
                </div>
              </div>

              {importResults?.successful > 0 && (
                <div style={modalStyles.successBox}>
                  <CheckCircle
                    size={20}
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: "600" }}>
                    {t("successMessage").replace(
                      "{count}",
                      importResults.successful,
                    )}
                  </span>
                </div>
              )}

              {importResults?.errors && importResults.errors.length > 0 && (
                <div
                  style={{
                    border: `1px solid ${currentTheme.colors.danger}30`,
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "12px 16px",
                      background: `${currentTheme.colors.danger}10`,
                      borderBottom: `1px solid ${currentTheme.colors.danger}30`,
                      fontWeight: "600",
                      color: currentTheme.colors.danger,
                      fontSize: "14px",
                    }}
                  >
                    Errors ({importResults.errors.length})
                  </div>
                  <div
                    style={{
                      padding: "16px",
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {importResults.errors.map((error, index) => (
                      <div
                        key={index}
                        style={{
                          padding: "12px",
                          background: currentTheme.colors.inputBackground,
                          borderRadius: "8px",
                          marginBottom: "8px",
                          border: `1px solid ${currentTheme.colors.border}`,
                        }}
                      >
                        <div
                          style={{
                            color: currentTheme.colors.textPrimary,
                            fontWeight: "600",
                            marginBottom: "4px",
                            fontSize: "13px",
                          }}
                        >
                          {t("row")} {error.row} - {t("employee")}{" "}
                          {error.clientCode || error.infinityCode || t("unknown")}
                        </div>
                        <div
                          style={{
                            color: currentTheme.colors.danger,
                            fontSize: "12px",
                          }}
                        >
                          {error.error || error.errors?.join(", ")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={modalStyles.infoBox}>
                <Info
                  size={18}
                  style={{ marginRight: "8px", verticalAlign: "middle" }}
                />
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  {t("recordsCreated")}
                </span>
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div
        style={modalStyles.overlay}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div style={modalStyles.modal}>
          {/* Header */}
          <div style={modalStyles.header}>
            <h1 style={modalStyles.title}>
              <Download size={28} />
              {t("importPayroll")}
            </h1>
            {/* <button
              style={modalStyles.closeButton}
              onClick={onClose}
              disabled={importLoading}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${currentTheme.colors.danger}10`;
                e.currentTarget.style.borderColor = currentTheme.colors.danger;
                e.currentTarget.style.color = currentTheme.colors.danger;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = currentTheme.colors.border;
                e.currentTarget.style.color = currentTheme.colors.textSecondary;
              }}
            >
              <X size={20} />
            </button> */}
          </div>

          {/* Step Indicator */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              padding: "20px 32px",
              borderBottom: `1px solid ${currentTheme.colors.border}`,
              background: currentTheme.colors.inputBackground,
            }}
          >
            {[
              { number: 1, titleKey: "upload", icon: Download },
              { number: 2, titleKey: "preview", icon: Eye },
              { number: 3, titleKey: "results", icon: CheckCircle },
            ].map((step, index) => (
              <React.Fragment key={step.number}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    background:
                      importStep >= step.number
                        ? currentTheme.colors.primary
                        : "transparent",
                    color:
                      importStep >= step.number
                        ? "#fff"
                        : currentTheme.colors.textSecondary,
                    border: `1px solid ${
                      importStep >= step.number
                        ? currentTheme.colors.primary
                        : currentTheme.colors.border
                    }`,
                    fontSize: "13px",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: "700",
                      background:
                        importStep >= step.number
                          ? "#fff"
                          : currentTheme.colors.border,
                      color:
                        importStep >= step.number
                          ? currentTheme.colors.primary
                          : currentTheme.colors.textSecondary,
                    }}
                  >
                    {step.number}
                  </div>
                  <step.icon size={16} />
                  {t(step.titleKey)}
                </div>
                {index < 2 && (
                  <div
                    style={{
                      width: "40px",
                      height: "2px",
                      background:
                        importStep > step.number
                          ? currentTheme.colors.primary
                          : currentTheme.colors.border,
                      transition: "all 0.3s ease",
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Progress Bar */}
          {importLoading && importProgress > 0 && (
            <div
              style={{
                padding: "16px 32px",
                background: currentTheme.colors.inputBackground,
                borderBottom: `1px solid ${currentTheme.colors.border}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{
                    color: currentTheme.colors.textPrimary,
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  Importing payroll records...
                </span>
                <span
                  style={{
                    color: currentTheme.colors.primary,
                    fontWeight: "700",
                    fontSize: "14px",
                  }}
                >
                  {importProgress}%
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "6px",
                  background: currentTheme.colors.border,
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${importProgress}%`,
                    height: "100%",
                    background: ` (90deg, ${currentTheme.colors.primary}, ${currentTheme.colors.success})`,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          )}

          {/* Content */}
          <div style={modalStyles.content}>{renderStepContent()}</div>

          {/* Footer */}
          <div style={modalStyles.footer}>
            {importStep === 2 && (
              <>
                <Mo3tiButton
                  variant="secondary"
                  onClick={() => {
                    setImportStep(1);
                    setImportData(null);
                  }}
                  disabled={importLoading}
                >
                  Back
                </Mo3tiButton>
                <Mo3tiButton
                  variant="primary"
                  icon={Download}
                  onClick={handleImport}
                  disabled={
                    importLoading || !importData || importData.length === 0
                  }
                >
                  {importLoading
                    ? "Importing..."
                    : `Import ${
                        importData?.filter((r) => r.isValid).length || 0
                      } Records`}
                </Mo3tiButton>
              </>
            )}

            {importStep === 3 && (
              <Mo3tiButton
                variant="primary"
                icon={CheckCircle}
                onClick={() => {
                  onImportComplete();
                }}
              >
                Done
              </Mo3tiButton>
            )}
          </div>
        </div>
      </div>
    );
  };

export default ExcelImportModal;